import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnInit,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

import { CustomInputComponentAbstract, makeCustomInputProviders } from './custom-input-component.abstract';

export enum InputTypes {
  TEXT = 'text',
  NUMBER = 'number',
  EMAIL = 'email',
  PASSWORD = 'password',
  SEARCH = 'search'
}

export type InputType = Lowercase<InputTypes>;

function getAutoCompleteValue(val: boolean | string): string {
  if (typeof val === 'string') {
    return val;
  }
  return val ? 'on' : 'off';
}

function trimValueAccessor(valueAccessor: ControlValueAccessor): void {
  const registerFn = valueAccessor.registerOnChange;
  valueAccessor.registerOnChange = (fn: (_: unknown) => void) =>
    registerFn.call(valueAccessor, (value: string) => fn(value?.trim()));
}

@Component({
  selector: 'shrd-uic-input-field',
  templateUrl: './input-field.component.html',
  styleUrls: ['./input-field.component.scss'],
  providers: makeCustomInputProviders(InputFieldComponent),
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InputFieldComponent<TValue = string>
  extends CustomInputComponentAbstract<TValue>
  implements OnInit, AfterViewInit
{
  autocompleteValue = 'on';
  private inputType: InputType = InputTypes.TEXT;
  protected trimSpaces = true;

  @ViewChild('inputRef', {
    static: true,
    read: NgControl
  })
  private readonly ngControl: NgControl;

  @HostBinding('class.shrd-uic-input-field')
  private readonly hostClass = true;

  @Input()
  set type(type: InputType) {
    this.inputType = type;
    this.cdr.markForCheck();
  }
  get type(): InputType {
    return this.inputType;
  }
  @Input() placeholder = '';
  @Input() screenReaderLabel: string;
  @Input() ariaLabel: string;
  @Input() useScreenReaderLabel = false;
  @Input() autofocus = false;
  @Input() autofocusPreventScroll = false;
  @Input() maxlength: number;
  @Input() pattern: RegExp | string;
  @Input() min: number;
  @Input() max: number;
  @Input()
  set autocomplete(ac: boolean | string) {
    this.autocompleteValue = getAutoCompleteValue(ac);
    this.cdr.markForCheck();
  }
  @Input() inputClasses = '';

  override ngOnInit(): void {
    super.ngOnInit();

    if (this.trimSpaces) {
      trimValueAccessor(this.ngControl.valueAccessor);
    }
  }

  ngAfterViewInit(): void {
    if (this.autofocus) {
      this.focus(this.autofocusPreventScroll);
    }
  }

  public focus(preventScroll = false): void {
    const inputEle = this.inputElementRef.nativeElement;
    inputEle.focus({ preventScroll });
  }

  public blur(): void {
    const inputEle = this.inputElementRef.nativeElement;
    inputEle.blur();
  }
}
